import { Check, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'

export type SortMode = 'recommended' | 'rating' | 'distance'

interface Props {
  open: boolean
  current: SortMode
  distanceAvailable: boolean
  /** #8 おすすめスコアが取得できている時だけ「おすすめ順」を選択可にする */
  recommendedAvailable: boolean
  onClose: () => void
  onSelect: (mode: SortMode) => void
}

/**
 * 並び順を選択する中央モーダル。distanceAvailable が false の場合は
 * 距離順を disabled 表示にする（位置情報未許可時など）。
 * recommendedAvailable が false の場合はおすすめ順を disabled 表示にする
 * （未集計・APIエラー時）。
 */
export default function SortModal({
  open,
  current,
  distanceAvailable,
  recommendedAvailable,
  onClose,
  onSelect,
}: Props) {
  const { t } = useI18n()

  if (!open) return null

  const options: { mode: SortMode; label: string; disabled: boolean }[] = [
    { mode: 'recommended', label: t('stores.sortByRecommended'), disabled: !recommendedAvailable },
    { mode: 'rating', label: t('stores.sortByRating'), disabled: false },
    { mode: 'distance', label: t('stores.sortByDistance'), disabled: !distanceAvailable },
  ]

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 z-[60] animate-fade-in"
      />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[360px] rounded-2xl bg-white z-[60] animate-scale-in shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="font-display text-lg font-semibold text-text-primary">
            {t('stores.sortLabel')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="w-10 h-10 flex items-center justify-center rounded-full tap-effect hover:bg-gray-50"
          >
            <X size={20} className="text-text-secondary" />
          </button>
        </div>
        <div className="flex flex-col">
          {options.map((opt) => (
            <button
              key={opt.mode}
              type="button"
              disabled={opt.disabled}
              onClick={() => {
                if (!opt.disabled) {
                  onSelect(opt.mode)
                  onClose()
                }
              }}
              className={`tap-effect flex items-center justify-between px-5 py-4 font-body text-base border-t border-gray-100 ${
                opt.disabled
                  ? 'text-text-disabled cursor-not-allowed'
                  : current === opt.mode
                    ? 'text-yokoso-red font-medium'
                    : 'text-text-primary'
              }`}
            >
              <span>{opt.label}</span>
              {current === opt.mode && !opt.disabled && (
                <Check size={20} className="text-yokoso-red" />
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  )
}
